import { Product } from "../../../contexts/cartContext/cartType";
import { AxiosInstance } from "axios";

/**
 * Tipo de resposta da validação de cupom
 */
interface ValidateCouponResponse {
  success: boolean;
  data: any;
  errorMsg?: string;
}

/**
 * Tipo de resposta da validação do carrinho
 */
interface ValidateCartResponse {
  success: boolean;
  msg: string;
}

/**
 * Tipo de resposta do cálculo de desconto
 */
interface CalculateDiscountResponse {
  success: boolean;
  discountedProducts: Product[];
  totalDiscount: number;
  errorMsg?: string;
}

/**
 * Valida se o cupom existe e é válido no servidor
 * @param couponName - Nome do cupom
 * @param api - Instância Axios
 * @param token - Token de autenticação
 * @param showAlert - Se false, não mostra toast de erro
 * @returns Resposta com sucesso e dados do cupom
 */
export async function validateDiscoutCoupon(
  couponName: string,
  api: AxiosInstance,
  token: string,
  showAlert?: boolean,
): Promise<ValidateCouponResponse> {
  try {
    if (!couponName) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      nameCoupom: couponName,
    };

    const response = await api
      .post("/api/discount-coupons/validCupons", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          return {
            success: true,
            data: response.data,
          };
        }
        return {
          success: false,
          data: [],
        };
      })
      .catch((err) => {
        let erro = "Cupom inválido";

        if (
          err.response &&
          err.response.data &&
          err.response.data.message &&
          err.response.data.message !=
            'Cannot read properties of undefined (reading "message")'
        ) {
          erro = err.response.data.message;
        }

        if (err.response && err.response.data && err.response.data.message) {
          switch (err.response.data.message) {
            case "Nenhum registro encontrado":
              erro = "Cupom não encontrado";
              break;
          }
        }

        return {
          success: false,
          data: [],
          errorMsg: erro,
        };
      });

    return response;
  } catch (error) {
    return {
      success: false,
      data: [],
      errorMsg: "Cupom inválido",
    };
  }
}

/**
 * Valida se o carrinho é válido para aplicação de cupom
 * Verifica se nenhum produto tem preço promocional ativo
 * @param products - Array de produtos do carrinho
 * @returns Validação e mensagem
 */
export function validateCartToCupom(products: Product[]): ValidateCartResponse {
  try {
    // Verifica se existe pelo menos 1 produto com promotionalPrice diferente de unitPrice
    const hasPromotional = products.some(
      (product) =>
        product.promotionalPrice &&
        product.promotionalPrice !== product.unitPrice,
    );

    if (hasPromotional) {
      return {
        success: false,
        msg: "Cupom não é aplicável pois existem itens promocionais no seu carrinho",
      };
    }

    return {
      success: true,
      msg: "Carrinho válido para aplicação de cupom",
    };
  } catch (error) {
    return {
      success: false,
      msg: "Erro ao validar carrinho",
    };
  }
}

/**
 * Calcula desconto do motor Ninegrid
 * @param couponData - Dados do cupom validado
 * @param products - Produtos do carrinho
 * @param api - Instância Axios
 * @param token - Token de autenticação
 * @param pluginspaceId - ID da filial/pluginspace
 * @returns Produtos descountados e valor total do desconto
 */
export async function calculateNinegridDiscount(
  couponData: any,
  products: Product[],
  api: AxiosInstance,
  token: string,
  pluginspaceId?: number,
): Promise<CalculateDiscountResponse> {
  try {
    // Transformar produtos para formato esperado pelo motor
    const productsToGiveDiscout = products.map((product) => ({
      hash: window.btoa(product.code),
      id: product.id,
      productName: product.name,
      image: product.image,
      stock: product.stock,
      code: product.code,
      locationId: product.locationId,
      quantity: product.quantity,
      oldDiscountPrice: product.oldDiscountPrice
        ? product.oldDiscountPrice
        : product.promotionalPrice,
      price: parseInt((product.unitPrice * 100).toFixed(0)),
      unitPrice: parseInt((product.unitPrice * 100).toFixed(0)),
      codFilial: pluginspaceId,
      discountValue: 0,
      discountPercentageValue: 0,
      info: "",
      chair: "",
      notes: "",
      productSku: product.sku,
      measures: product.measures,
      variation: Object.entries(product.variation).map((obj) => ({
        grid: obj[0],
        value: obj[1],
      })),
    }));

    const payload = {
      couponPromotionalCode: couponData.nameCoupom,
      totalAmountDiscount: parseFloat(couponData.valueDiscount),
      percentageDiscountTotalAmount: parseFloat(couponData.percentageDiscount),
      discountGeneral: 0,
      products: productsToGiveDiscout,
    };

    try {
      const response = await api.post(
        "/api/discount-enginer/calcdiscount",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data) {
        const data = response.data.data.data;
        let totalDescontoCupom = 0;

        if (data.totalAmountDiscount) {
          totalDescontoCupom = data.totalAmountDiscount;
        } else if (data.percentageDiscountTotalAmount) {
          const totalDesconto = data.products.reduce(
            (total: number, product: any) => {
              if (product.price !== product.unitPrice) {
                const totalProdutoPreco = product.unitPrice * product.quantity;
                const descontoProdutoTotal = product.price;
                const descontoPorProduto =
                  totalProdutoPreco - descontoProdutoTotal;
                return total + descontoPorProduto;
              }
              return total;
            },
            0,
          );
          totalDescontoCupom = parseFloat((totalDesconto / 100).toFixed(2));
        }

        // Mapear produtos descountados de volta para o formato Product
        const discountedProducts: Product[] = data.products
          .map((discountProduct: any) => {
            const originalProduct = products.find(
              (p) => p.id === discountProduct.id,
            );
            if (!originalProduct) return null;

            const discountedPrice = parseFloat(
              (
                (discountProduct.unitPrice - discountProduct.discount) /
                100
              ).toFixed(2),
            );

            return {
              ...originalProduct,
              promotionalPrice: discountedPrice,
              oldDiscountPrice:
                originalProduct.oldDiscountPrice ||
                originalProduct.promotionalPrice ||
                originalProduct.unitPrice,
              cupomDiscount: discountedPrice,
              withDiscout: true,
            };
          })
          .filter(Boolean) as Product[];

        return {
          success: true,
          discountedProducts,
          totalDiscount: totalDescontoCupom,
        };
      }

      return {
        success: false,
        discountedProducts: [],
        totalDiscount: 0,
        errorMsg: "Cupom não foi aplicado",
      };
    } catch (err) {
      return {
        success: false,
        discountedProducts: [],
        totalDiscount: 0,
        errorMsg: "Cupom não foi aplicado",
      };
    }
  } catch (error) {
    return {
      success: false,
      discountedProducts: [],
      totalDiscount: 0,
      errorMsg: "Erro ao calcular desconto",
    };
  }
}

/**
 * Calcula desconto do motor TOTVS Promotion Engine
 * @param coupon - Nome do cupom
 * @param products - Produtos do carrinho
 * @param api - Instância Axios
 * @param token - Token de autenticação
 * @param pluginspaceId - ID da filial/pluginspace
 * @returns Produtos descountados
 */
export async function calculateTotvsDiscount(
  coupon: string,
  products: Product[],
  api: AxiosInstance,
  token: string,
  pluginspaceId?: number,
): Promise<CalculateDiscountResponse> {
  try {
    // Transformar produtos para formato esperado pelo motor TOTVS
    const productsPayload = products.map((product) => ({
      hash: window.btoa(product.code),
      id: product.id,
      productName: product.name,
      image: product.image,
      stock: product.stock,
      code: product.code,
      locationId: product.locationId,
      quantity: product.quantity,
      discount:
        product.promotionalPrice !== product.unitPrice &&
        product.promotionalPrice !== 0
          ? parseInt(
              ((product.unitPrice - product.promotionalPrice) * 100).toFixed(0),
            )
          : 0,
      price:
        product.promotionalPrice !== product.unitPrice &&
        product.promotionalPrice !== 0
          ? parseInt(
              ((product.unitPrice - product.promotionalPrice) * 100).toFixed(0),
            )
          : parseInt((product.unitPrice * 100).toFixed(0)),
      unitPrice: parseInt((product.unitPrice * 100).toFixed(0)),
      codFilial: pluginspaceId,
      info: "",
      chair: "",
      notes: "",
      productSku: product.sku,
      variation: Object.entries(product.variation).map((obj) => ({
        grid: obj[0],
        value: obj[1],
      })),
    }));

    const response = await api.post(
      "/api/totvsPromotionEngine/calculatePromotion",
      {
        "couponPromotionalCode--": coupon,
        discountGeneral: 0.0,
        products: productsPayload,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (response.data && response.data.products) {
      const discountedProducts: Product[] = response.data.products
        .map((product: any) => {
          const originalProduct = products.find((p) => p.id === product.id);
          if (!originalProduct) return null;

          const objVar: any = {};
          product.variation.forEach((variation: any) => {
            objVar[variation.grid] = variation.value;
          });

          const promotionalPrice = product.price
            ? parseFloat(
                ((product.unitPrice - product.discount) / 100).toFixed(2),
              )
            : parseFloat(
                ((product.unitPrice - product.discount) / 100).toFixed(2),
              );

          return {
            id: product.id,
            name: product.productName,
            image: product.image,
            unitPrice: parseFloat((product.unitPrice / 100).toFixed(2)),
            promotionalPrice,
            quantity: product.quantity,
            stock: product.stock,
            sku: product.productSku,
            variation: objVar,
            code: product.code,
            locationId: product.locationId,
            pluginspaceHash: originalProduct.pluginspaceHash,
            outStock: originalProduct.outStock,
            measures: originalProduct.measures,
          } as Product;
        })
        .filter(Boolean) as Product[];

      const totalDiscount = response.data.totalAmountDiscount || 0;

      return {
        success: true,
        discountedProducts,
        totalDiscount,
      };
    }

    return {
      success: false,
      discountedProducts: [],
      totalDiscount: 0,
      errorMsg: "Cupom não foi aplicado",
    };
  } catch (error) {
    console.error("Erro ao calcular desconto TOTVS:", error);
    return {
      success: false,
      discountedProducts: [],
      totalDiscount: 0,
      errorMsg: "Cupom não foi aplicado",
    };
  }
}
